'use client';

import { createContext, useContext, useRef, ReactNode } from 'react';
import { fbs } from 'fbtee';
import { ContentType, GenerationBatch } from '@/app/types';
import type { UpsellBannerData } from '@/app/types/quota';
import * as Sentry from '@sentry/nextjs';
import { QUOTA_EXCEEDED_ERROR } from '@/lib/constants/errors';

const ERR_TIMEOUT = () =>
  String(fbs('Generation timed out after 10 minutes', 'Video generation timed out'));
const ERR_STREAM = () =>
  String(fbs('Stream encountered an error', 'Video generation stream error'));

/** Map raw poll response items to the content update shape used by updateBatch. */
function mapPollItemsToContent(items: any[]) {
  return items.map((item: any) => ({
    id: item.id,
    type: 'videos' as ContentType,
    isLoading: item.isLoading,
    videoUrl: item.videoUrl,
    videoHandle: item.videoHandle,
    imageUrl: item.imageUrl,
    imageHandle: item.imageHandle,
    data: item.data,
    error: item.error,
  }));
}

// Client-side stream lifetime ceiling (10 min). Mirrors the server's 600s cap.
const POLLING_TIMEOUT_MS = 600000;

/**
 * Callbacks that can be passed when registering a streaming job.
 * These are called when the stream reaches a terminal state.
 */
export type PollingCallbacks = {
  onComplete?: (batchId: string) => void;
  onError?: (batchId: string, error: string) => void;
  onTimeout?: (batchId: string) => void;
  onProgress?: (batchId: string, items: any[]) => void;
  onQuotaExceeded?: (batchId: string, upsell: UpsellBannerData) => void;
};

/**
 * Internal representation of an active streaming job.
 */
interface PollingJob {
  batchId: string;
  callbacks: PollingCallbacks;
  updateBatch: (batchId: string, updates: Partial<GenerationBatch>) => void;
  refetchBatches?: () => Promise<void>;
  streamLifetimeTimeout: ReturnType<typeof setTimeout>;
  // Closure-scoped cleanup that flips the cleanedUp flag, suppressing the
  // spurious browser-dispatched 'error' event that can fire after es.close().
  // External close paths MUST call this, not es.close() directly.
  closeStream: () => void;
}

/**
 * Context value exposed to consumers
 */
type PollingContextValue = {
  registerPolling: (
    batchId: string,
    callbacks: PollingCallbacks,
    updateBatch: (batchId: string, updates: Partial<GenerationBatch>) => void,
    refetchBatches?: () => Promise<void>
  ) => void;
  stopPolling: (batchId: string) => void;
  isPolling: (batchId: string) => boolean;
  getActivePolls: () => string[];
};

// Default context value (prevents crashes if used outside provider)
const defaultValue: PollingContextValue = {
  registerPolling: () => {},
  stopPolling: () => {},
  isPolling: () => false,
  getActivePolls: () => [],
};

// Private context
const PollingContext = createContext<PollingContextValue>(defaultValue);

/**
 * Provider component for global generation-batch stream management.
 *
 * Placed at the root layout level so it never unmounts
 * during page navigation. This allows the SSE stream to continue even when the
 * user navigates away from the page that started the generation.
 *
 * Features:
 * - Maintains streams across page navigation
 * - Timeout with callback notification
 * - Supports multiple concurrent streaming jobs
 * - Callbacks for complete/error/timeout states
 */
export function PollingProvider({ children }: { children: ReactNode }) {
  // Use a ref to store active polls so we don't trigger re-renders
  const activePolls = useRef<Map<string, PollingJob>>(new Map());

  const registerPolling = (
    batchId: string,
    callbacks: PollingCallbacks,
    updateBatch: (batchId: string, updates: Partial<GenerationBatch>) => void,
    refetchBatches?: () => Promise<void>
  ) => {
    // Tear down any existing stream for this batch before starting a new one.
    const existingJob = activePolls.current.get(batchId);
    if (existingJob) {
      existingJob.closeStream();
    }

    const es = new EventSource(`/api/generation-batches/${batchId}/stream`);

    // `cleanedUp` is read by the error handler to suppress the post-close
    // error event some browsers fire after the server initiates FIN. Without
    // this guard, every successful SSE batch would show "Stream encountered
    // an error" right after the terminal frame already fired onComplete.
    let cleanedUp = false;
    const cleanup = () => {
      cleanedUp = true;
      const j = activePolls.current.get(batchId);
      if (j?.streamLifetimeTimeout) clearTimeout(j.streamLifetimeTimeout);
      es.close();
      activePolls.current.delete(batchId);
    };

    // Client-side ceiling matching POLLING_TIMEOUT_MS — server's 600s ceiling
    // covers normal operation; this guards against EventSource reconnect storms
    // or proxies that keep the socket open without delivering events.
    const lifetimeTimeout = setTimeout(() => {
      // Tear down BEFORE the consumer callback: cleanup() only needs batchId,
      // so a throwing onTimeout must not be able to skip teardown (which would
      // leave the EventSource open + the job in activePolls).
      updateBatch(batchId, {
        hasError: true,
        error: ERR_TIMEOUT(),
        isComplete: true,
        generationEndTime: new Date(),
      });
      cleanup();
      callbacks.onTimeout?.(batchId);
    }, POLLING_TIMEOUT_MS);

    const job: PollingJob = {
      batchId,
      callbacks,
      updateBatch,
      refetchBatches,
      streamLifetimeTimeout: lifetimeTimeout,
      closeStream: cleanup,
    };
    activePolls.current.set(batchId, job);

    es.addEventListener('message', (event) => {
      // Guard ONLY the JSON.parse: a malformed frame is a stream-integrity
      // failure and must flip the batch to errored. The frame dispatch +
      // consumer callbacks below run OUTSIDE this try on purpose — a consumer
      // callback (onComplete/onProgress/onQuotaExceeded) that throws AFTER a
      // successful terminal frame must NOT be re-mapped into a false stream
      // error (which would double-fire onError and flip success→error).
      let frame: any;
      try {
        frame = JSON.parse(event.data);
      } catch (err) {
        // Tear down + report BEFORE the consumer callback: a throwing onError
        // must not skip cleanup() or the Sentry capture (both independent of
        // the callback). cleanup() only needs batchId.
        const errorMessage = ERR_STREAM();
        updateBatch(batchId, {
          hasError: true,
          error: errorMessage,
          isComplete: true,
          generationEndTime: new Date(),
        });
        cleanup();
        Sentry.captureException(err, {
          tags: { component: 'PollingContext', operation: 'streaming' },
          extra: { batchId },
        });
        callbacks.onError?.(batchId, errorMessage);
        return;
      }

      if (frame.type === 'timeout') {
        // Tear down BEFORE the consumer callback (cleanup() only needs batchId)
        // so a throwing onTimeout can't skip teardown.
        updateBatch(batchId, {
          hasError: true,
          error: ERR_TIMEOUT(),
          isComplete: true,
          generationEndTime: new Date(),
        });
        cleanup();
        callbacks.onTimeout?.(batchId);
        return;
      }
      if (frame.type === 'error') {
        // Server emits a code (not a localized message) so we always
        // substitute the fbs-wrapped string for the user's locale.
        // Tear down BEFORE the consumer callback (cleanup() only needs batchId)
        // so a throwing onError can't skip teardown.
        const errorMessage = ERR_STREAM();
        updateBatch(batchId, {
          hasError: true,
          error: errorMessage,
          isComplete: true,
          generationEndTime: new Date(),
        });
        cleanup();
        callbacks.onError?.(batchId, errorMessage);
        return;
      }
      if (frame.type === 'partial_reconcile') {
        // Server detached without a definitive terminal state. Don't lie:
        //   - no `isComplete: true` / `hasError: true` (we don't know)
        //   - no `onComplete` (consumers treat as success → false toast)
        //   - no `onError` (we don't know there's an error → false toast)
        // Per-item DB writes from applyTerminalEventToItem persisted the
        // truth; refetching the batches list surfaces fresh state. The
        // fuse is cleared via cleanup() — if the gen is genuinely stuck,
        // the next user navigation will catch it. Accepted v1 trade-off.
        refetchBatches?.().catch(() => {});
        cleanup();
        return;
      }
      // Snapshot frame (PollResponse shape).
      const items = frame.items ?? [];
      if (frame.isComplete) {
        // Quota gate parity with the server snapshot's quota handling.
        if (callbacks.onQuotaExceeded) {
          const hasQuotaExceeded =
            frame.upsell &&
            Array.isArray(items) &&
            items.length > 0 &&
            items.some((item: any) => item.error === QUOTA_EXCEEDED_ERROR);
          if (hasQuotaExceeded) {
            // Tear down BEFORE the consumer callback (cleanup() only needs
            // batchId) so a throwing onQuotaExceeded can't skip teardown.
            refetchBatches?.().catch(() => {});
            cleanup();
            callbacks.onQuotaExceeded(batchId, frame.upsell);
            return;
          }
        }
        // Tear down BEFORE the consumer callback (cleanup() only needs batchId)
        // so a throwing onComplete can't skip teardown — otherwise the stream
        // stays open, the job lingers in activePolls, and the lifetime fuse
        // later false-flips the already-succeeded batch to errored.
        updateBatch(batchId, {
          content: mapPollItemsToContent(items),
          isComplete: true,
          generationEndTime: new Date(),
        });
        refetchBatches?.().catch(() => {});
        cleanup();
        callbacks.onComplete?.(batchId);
      } else {
        updateBatch(batchId, { content: mapPollItemsToContent(items) });
        callbacks.onProgress?.(batchId, items);
      }
    });

    es.addEventListener('error', () => {
      if (cleanedUp) return;
      if (es.readyState !== EventSource.CLOSED) return;
      Sentry.captureException(new Error('EventSource closed'), {
        tags: { component: 'PollingContext', operation: 'streaming' },
        extra: { batchId },
      });
      // Tear down BEFORE the consumer callback (cleanup() only needs batchId)
      // so a throwing onError can't skip teardown.
      const errorMessage = ERR_STREAM();
      updateBatch(batchId, {
        hasError: true,
        error: errorMessage,
        isComplete: true,
        generationEndTime: new Date(),
      });
      cleanup();
      callbacks.onError?.(batchId, errorMessage);
    });
  };

  const stopPolling = (batchId: string) => {
    const job = activePolls.current.get(batchId);
    if (job) {
      job.closeStream();
    }
  };

  const isPolling = (batchId: string): boolean => {
    return activePolls.current.has(batchId);
  };

  const getActivePolls = (): string[] => {
    return Array.from(activePolls.current.keys());
  };

  const value: PollingContextValue = {
    registerPolling,
    stopPolling,
    isPolling,
    getActivePolls,
  };

  return <PollingContext.Provider value={value}>{children}</PollingContext.Provider>;
}

/**
 * Hook to access the polling context.
 *
 * @example
 * const { registerPolling, stopPolling, isPolling } = usePollingContext();
 *
 * // Register a polling job with callbacks
 * registerPolling(batchId, {
 *   onComplete: () => toast.success('Done!'),
 *   onError: (_, error) => toast.error(error),
 *   onTimeout: () => toast.error('Timed out'),
 * }, updateBatch, refetchBatches);
 */
export function usePollingContext() {
  return useContext(PollingContext);
}
